import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Bell,
  User,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Pill,
  CreditCard,
  Users,
  Shield,
  Lock,
  Headset,
  CalendarHeart,
  Globe,
  Search,
  Stethoscope,
  Tags,
  Dumbbell,
  ClipboardPlus,
  CalendarDays,
  Baby,
  Flower2,
  Brain,
  Watch,
  FileText,
  HeartPulse,
  Activity,
  ClipboardList,
  ShieldPlus,
  MapPin,
  BellRing,
  Gift,
  UserRound,
  HandHeart,
  Sparkles,
  CheckCheck,
  Scale,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../../i18n/i18n';
import MuiMenu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useServiceAccess } from '../../context/ServiceAccessContext';
import { serviceSlugForPath } from '../../config/serviceAccess';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import WalletChip from '../ServiceWallet/WalletChip';
import { useServiceWallet } from '../ServiceWallet/WalletContext';
import './Header.css';

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  is_read: boolean;
  item_type: string;
  action_url?: string | null;
  created_at: string;
  day?: string;
  date?: string;
  time?: string;
  is_today?: boolean;
}

type NotificationFilter = 'all' | 'unread';

interface SearchEntry {
  label: string;
  keywords: string[];
  route: string;
  icon: React.ElementType;
  serviceSlug?: string;
}

const SEARCH_INDEX: SearchEntry[] = [
  { label: 'AI Family Doctor', keywords: ['ai', 'doctor', 'family doctor', 'ai doctor', 'chat', 'assistant', 'symptoms', 'advice', 'voice'], route: '/ai-family-doctor', icon: Stethoscope, serviceSlug: 'ai-family-doctor' },
  { label: 'Doctors & Video Consultation', keywords: ['doctor', 'doctors', 'consultation', 'teleconsultation', 'video consult', 'consult', 'specialist', 'appointment', 'tele'], route: '/teleconsultation', icon: Stethoscope, serviceSlug: 'tele-consultation' },
  { label: 'Health Records', keywords: ['records', 'reports', 'prescription', 'lab', 'bills', 'medical', 'medicine', 'treatment', 'vaccination', 'health records', 'history'], route: '/health-records', icon: ClipboardPlus, serviceSlug: 'health-records' },
  { label: 'Health Assessment', keywords: ['assessment', 'health check', 'checkup', 'questionnaire', 'quiz', 'test'], route: '/health-assessment', icon: HeartPulse, serviceSlug: 'health-assessment' },
  { label: 'Health Insights / Summary', keywords: ['health summary', 'insights', 'health insights', 'analytics', 'overview', 'dashboard', 'report'], route: '/health-summary', icon: Activity, serviceSlug: 'health-insights' },
  { label: 'Smart Reports', keywords: ['smart report', 'smart reports', 'analytics', 'lab reports', 'insights'], route: '/smart-reports', icon: FileText, serviceSlug: 'smart-reports' },
  { label: 'Symptoms Checker', keywords: ['symptom', 'symptoms', 'checker', 'diagnosis', 'tracker', 'ailment'], route: '/symptoms-checker', icon: ClipboardList, serviceSlug: 'symptoms-tracker' },
  { label: 'Disease Prediction', keywords: ['disease', 'prediction', 'predict', 'risk', 'diabetes', 'heart', 'cancer'], route: '/disease-prediction', icon: Brain, serviceSlug: 'disease-prediction' },
  { label: 'Medicine Reminder', keywords: ['medicine', 'medication', 'medicines', 'pill', 'dose', 'reminder', 'drugs'], route: '/my-reminders', icon: HandHeart, serviceSlug: 'medicine-reminder' },
  { label: 'Insurance Management', keywords: ['insurance', 'insurances', 'policy', 'policies', 'claims', 'coverage', 'health insurance'], route: '/insurance-record', icon: ShieldPlus, serviceSlug: 'insurance-management' },
  { label: 'Group Mediclaim', keywords: ['mediclaim', 'group mediclaim', 'corporate', 'claim', 'reimbursement'], route: '/my-mediclaim', icon: ShieldPlus, serviceSlug: 'group-mediclaim' },
  { label: 'Wearable Devices', keywords: ['wearable', 'device', 'devices', 'watch', 'fitbit', 'samsung health', 'measurement', 'steps'], route: '/wearable-devices', icon: Watch, serviceSlug: 'wearable-devices' },
  { label: 'Gym & Fitness Services', keywords: ['gym', 'fitness', 'workout', 'training', 'gym partner', 'exercise', 'zumba'], route: '/gymservices', icon: Dumbbell, serviceSlug: 'gym-services' },
  { label: 'Discounts & Offers', keywords: ['voucher', 'vouchers', 'discount', 'discounts', 'offers', 'coupon', 'deals', 'provider', 'vendor', 'partner'], route: '/offers', icon: Tags, serviceSlug: 'discount-vouchers' },
  { label: 'Health Discounts / Partners', keywords: ['health discount', 'health offers', 'partners', 'providers', 'network'], route: '/discounts/all', icon: HeartPulse, serviceSlug: 'discount-vouchers' },
  { label: 'Sponsored Packages', keywords: ['package', 'packages', 'sponsored', 'plan', 'membership', 'offers'], route: '/sponsored-packages', icon: Gift, serviceSlug: 'sponsored-packages' },
  { label: "Women's Health", keywords: ['women', 'womens', 'female', 'pregnancy', 'cycle', 'menopause', 'gynecology', 'women health'], route: '/womens-health', icon: Flower2, serviceSlug: 'women-health' },
  { label: 'Child Development', keywords: ['child', 'kids', 'baby', 'development', 'milestone', 'vaccination', 'children'], route: '/child-development', icon: Baby, serviceSlug: 'child-development' },
  { label: 'Elderly Care', keywords: ['elderly', 'senior', 'aged', 'care', 'geriatric', 'old age'], route: '/elderly-care', icon: HandHeart, serviceSlug: 'elderly-care' },
  { label: 'Mental Wellness', keywords: ['mental', 'mind', 'stress', 'anxiety', 'mood', 'meditation', 'breathing', 'wellness'], route: '/mental-wellness', icon: Brain, serviceSlug: 'mental-wellness' },
  { label: 'Wellness Calendar', keywords: ['wellness', 'calendar', 'activity', 'events', 'challenges'], route: '/wellness-calendar', icon: CalendarDays },
  { label: 'Reminders & Tasks', keywords: ['reminder', 'reminders', 'tasks', 'todo', 'notes', 'medication'], route: '/reminders', icon: BellRing },
  { label: 'Notifications', keywords: ['notification', 'notifications', 'alerts', 'messages'], route: '/notifications', icon: BellRing },
  { label: 'My Bookings', keywords: ['booking', 'bookings', 'my bookings', 'appointment', 'appointments', 'consultation', 'tele consultation'], route: '/my-bookings', icon: CalendarDays },
  { label: 'My Payments', keywords: ['payment', 'payments', 'transaction', 'transactions', 'billing', 'invoice', 'receipt', 'subscription'], route: '/my-transactions', icon: FileText },
  { label: 'Address Book', keywords: ['address', 'addresses', 'address book', 'location', 'delivery'], route: '/my-address-book', icon: MapPin },
  { label: 'Profile & Settings', keywords: ['profile', 'settings', 'account', 'edit profile', 'personal'], route: '/profile', icon: UserRound },
  { label: 'Privacy', keywords: ['privacy', 'data', 'consent', 'permissions'], route: '/privacy', icon: HeartPulse },
  { label: 'Security & MFA', keywords: ['security', 'mfa', 'password', 'two factor', '2fa', 'otp', 'authentication'], route: '/security/mfa', icon: ShieldPlus },
  { label: 'Support & Help', keywords: ['support', 'help', 'ticket', 'complaint', 'query', 'raise', 'feedback', 'contact'], route: '/support', icon: Sparkles },
  { label: 'AI & Health Insights Terms', keywords: ['ai', 'health insights', 'insights', 'disease prediction', 'symptoms', 'assessment', 'disclaimer', 'triage'], route: '/ai-health-terms', icon: FileText },
  { label: 'Terms of Use', keywords: ['terms', 'conditions', 'terms of use', 'legal', 'policy'], route: '/terms', icon: Scale },
  { label: 'Privacy Policy', keywords: ['privacy', 'policy', 'data protection'], route: '/privacy-policy', icon: FileText },
  { label: 'Service Policies', keywords: ['service policies', 'customer policies', 'customer information', 'refund', 'refunds', 'cancellation', 'chargeback', 'dispute', 'dpdp', 'gdpr', 'ccpa', 'hipaa', 'encryption', 'information security'], route: '/customer-policies', icon: FileText },
  { label: 'All Legal Documents', keywords: ['legal', 'legal documents', 'policies', 'terms', 'privacy'], route: '/legal', icon: Scale },

];

const Header: React.FC = () => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationFilter, setNotificationFilter] = useState<NotificationFilter>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isLoading, isIndividual } = useAuth();
  const { activeProfile, showProfileSwitcher, profiles, profilePhoto } = useProfile();
  const { canAccess } = useServiceAccess();
  const {
    provider: walletProvider,
    balance: walletBalance,
    loading: walletLoading,
    hidden: walletHidden,
    openCredits,
  } = useServiceWallet();
  const { t, i18n } = useTranslation('header');
  const [avatarFailed, setAvatarFailed] = useState(false);

  // Reset the image-failure fallback whenever the photo URL changes
  useEffect(() => {
    setAvatarFailed(false);
  }, [profilePhoto]);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.notifications, {
        params: { page_size: 25 },
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread || 0);
    } catch {
      // Silently fail - notifications are non-critical
    }
  }, []);

  const visibleNotifications = useMemo(
    () =>
      notifications.filter((n) => {
        if (!n.action_url) return true;
        const slug = serviceSlugForPath(n.action_url);
        return !slug || canAccess(slug);
      }),
    [notifications, canAccess],
  );

  const filteredNotifications = useMemo(() => {
    if (notificationFilter === 'unread') return visibleNotifications.filter((n) => !n.is_read);
    return visibleNotifications;
  }, [visibleNotifications, notificationFilter]);

  const filterCounts = useMemo(() => ({
    all: visibleNotifications.length,
    unread: visibleNotifications.filter((n) => !n.is_read).length,
  }), [visibleNotifications]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return SEARCH_INDEX.filter(
      (entry) =>
        (entry.label.toLowerCase().includes(q) ||
          entry.keywords.some((k) => k.toLowerCase().includes(q))) &&
        (!entry.serviceSlug || canAccess(entry.serviceSlug))
    ).slice(0, 8);
  }, [searchQuery, canAccess]);

  const handleGlobalSearch = (entry: SearchEntry) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(`${entry.route}?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const handleGlobalSearchSubmit = () => {
    if (searchResults.length > 0) {
      handleGlobalSearch(searchResults[0]);
    }
  };

  const notificationFilters: { key: NotificationFilter; label: string }[] = [
    { key: 'all', label: t('filters.all', { defaultValue: 'All' }) },
    { key: 'unread', label: t('filters.unread', { defaultValue: 'Unread' }) },
  ];

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await axiosInstance.post(`${API_ENDPOINTS.notifications}mark-all-read/`);
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch {
      // Silently fail
    }
  };

  const handleSwitchProfile = () => {
    setShowProfileMenu(false);
    showProfileSwitcher();
  };

  const handleChangeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setShowLangMenu(false);
  };

  const profileMenuItems = [
    { name: t('profileMenu.manageProfile'), link: '/profile', icon: User },
    ...(isIndividual ? [{ name: t('auth:plans.navLabel'), link: '/plans', icon: CreditCard }] : []),
    { name: t('profileMenu.myBookings'), link: '/my-bookings', icon: CalendarDays },
    { name: t('profileMenu.myPayments'), link: '/my-transactions', icon: CreditCard },
    ...(canAccess('medicine-reminder')
      ? [{ name: t('profileMenu.medicineReminder'), link: '/medicine-reminder', icon: Pill }]
      : []),
    { name: t('profileMenu.wellnessCalendar'), link: '/wellness-calendar', icon: CalendarHeart },
    { name: t('profileMenu.securityMFA'), link: '/security/mfa', icon: Lock },
    { name: t('profileMenu.privacy'), link: '/privacy', icon: Shield },
    ...(profiles.length > 1
      ? [{ name: t('profileMenu.switchProfile'), link: '#switch-profile', icon: Users, action: handleSwitchProfile }]
      : []),
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const isMobilePanelOpen = mobileMenuOpen || showProfileMenu || showNotifications || showLangMenu;
    const originalOverflow = document.body.style.overflow;
    if (isMobilePanelOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalOverflow;
    }
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [mobileMenuOpen, showProfileMenu, showNotifications, showLangMenu]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
      if (window.innerWidth >= 768 && (showProfileMenu || showNotifications)) {
        setShowProfileMenu(false);
        setShowNotifications(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen, showProfileMenu, showNotifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.notification-section')) {
        setShowNotifications(false);
      }
      if (!target.closest('.profile-section')) {
        setShowProfileMenu(false);
      }
      if (!target.closest('.lang-section')) {
        setShowLangMenu(false);
      }
      if (!target.closest('.header-global-search')) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setShowProfileMenu(false);
    await logout();
    navigate('/login');
  };

  const getUserDisplayName = () => {
    if (user?.name) {
      return user.name.split(' ')[0];
    }
    return 'User';
  };

  const getUserInitials = () => {
    if (user?.name) {
      const parts = user.name.split(' ');
      return parts.length > 1
        ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        : parts[0][0].toUpperCase();
    }
    return 'U';
  };

  // Photo when available (and still loadable); initials otherwise
  const avatarContent =
    profilePhoto && !avatarFailed ? (
      <img src={profilePhoto} alt="" className="avatar-img" onError={() => setAvatarFailed(true)} />
    ) : (
      <span>{getUserInitials()}</span>
    );

  const currentLang = i18n.resolvedLanguage?.split('-')[0] || 'en';
  return (
    <header>
      <nav className={`modern-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-inner">
          {/* Logo */}
          <Link to="/" className="header-logo">
            <img src="/images/logo.svg" alt="Infinicare" className="logo-img" />
          </Link>

          {/* Global Search */}
          <div className="header-global-search" ref={searchRef}>
            <div className="header-search-box">
              <Search size={16} className="header-search-icon" />
              <input
                type="text"
                className="header-search-input"
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => searchQuery.trim() && setSearchOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleGlobalSearchSubmit();
                  }
                  if (e.key === 'Escape') {
                    setSearchOpen(false);
                  }
                }}
              />
              {searchQuery && (
                <button
                  className="header-search-clear"
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setSearchQuery('');
                    setSearchOpen(false);
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {searchOpen && (
              <>
                <div className="header-search-overlay" onClick={() => setSearchOpen(false)} />
                <div className="header-search-dropdown">
                  {searchResults.length === 0 ? (
                    <div className="header-search-empty">
                      {searchQuery.trim() ? 'No matching sections found.' : 'Start typing to search across sections.'}
                    </div>
                  ) : (
                    searchResults.map((entry) => {
                      const Icon = entry.icon;
                      return (
                        <button
                          key={entry.route}
                          type="button"
                          className="header-search-item"
                          onClick={() => handleGlobalSearch(entry)}
                        >
                          <span className="header-search-item-icon">
                            <Icon size={16} />
                          </span>
                          <span className="header-search-item-label">{entry.label}</span>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>

          {/* Header Actions */}
          <div className="header-actions">
            {!walletHidden && (
              <WalletChip
                balance={walletBalance}
                onAdd={openCredits}
                label={t(`services:wallet.${walletProvider.labelKey}`)}
                addLabel={t('services:wallet.addCredits')}
                formatBalance={walletProvider.formatBalance}
                loading={walletLoading}
              />
            )}

            {/* Home */}
            <button
              className={`action-btn ${location.pathname === '/' ? 'active' : ''}`}
              title={t('home')}
              onClick={() => navigate('/')}
            >
              <Home size={20} />
            </button>

            {/* Help & Support */}
            <button
              className={`action-btn ${location.pathname.startsWith('/support') ? 'active' : ''}`}
              title={t('helpAndSupport')}
              onClick={() => navigate('/support')}
            >
              <Headset size={20} />
            </button>

            {/* Language Switcher */}
            <div className="lang-section">
              <button className="action-btn" onClick={handleOpen} title={t("common:language.switchLanguage")}>
                <Globe size={20} />
              </button>

              <MuiMenu anchorEl={anchorEl} open={open} onClose={handleClose}
                PaperProps={{
                  sx: { mt: 1, borderRadius: 3, minWidth: 220, boxShadow: "0 10px 30px rgba(0,0,0,0.12)", padding: "6px", },
                  }}
                >
                  <div style={{ padding: "10px 14px", fontWeight: 600, fontSize: "14px", borderBottom: "1px solid #e5e7eb", marginBottom: "4px", }}>
                    {t("common:language.switchLanguage")}
                  </div>

                  {Object.entries(supportedLanguages).map(([code, lang]) => (
                    <MenuItem
                      key={code}
                      selected={currentLang === code}
                      onClick={() => {
                        handleChangeLanguage(code);
                        handleClose();
                      }}
                      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, py: 1.2, px: 1.5, borderRadius: 2, marginBottom: "2px", }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", }}>
                        <span style={{ fontSize: "18px" }}> {lang.flag} </span>
                        <span style={{ fontSize: "14px", fontWeight: 500, }}> {lang.nativeLabel} </span>
                      </div>

                      {currentLang === code && (
                        <span style={{ color: "#0284c7", fontWeight: 700, }}> ✓ </span>
                      )}
                    </MenuItem>
                  ))}
                </MuiMenu>
              </div>

            {/* Notifications */}
            <div className="notification-section">
              <button
                className="action-btn"
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) fetchNotifications();
                }}
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="notification-dot">{unreadCount}</span>
                )}
              </button>
              {showNotifications && (
                <div className="dropdown-panel notification-panel">
                  <div className="panel-header">
                    <h6>{t('notifications')}</h6>
                    {unreadCount > 0 && (
                      <button className="panel-action-btn" onClick={handleMarkAllRead}>
                        <CheckCheck size={14} />
                        <span>{t('markAllRead')}</span>
                        <span className="panel-action-count">{unreadCount}</span>
                      </button>
                    )}
                  </div>
                  <div className="notification-filter-row" role="tablist" aria-label="Notification filters">
                    {notificationFilters.map(({ key, label }) => {
                      const count = filterCounts[key];
                      const active = notificationFilter === key;
                      return (
                        <Chip
                          key={key}
                          role="tab"
                          aria-selected={active}
                          label={count > 0 ? `${label} · ${count}` : label}
                          size="small"
                          onClick={() => setNotificationFilter(key)}
                          variant={active ? 'filled' : 'outlined'}
                          color={active ? 'primary' : 'default'}
                          sx={{
                            height: 26,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            borderRadius: '999px',
                            '& .MuiChip-label': { px: 1.1 },
                            ...(active
                              ? { bgcolor: '#06b6d4', color: '#fff', borderColor: '#06b6d4' }
                              : { color: '#64748b', borderColor: '#e2e8f0', bgcolor: '#fff' }),
                            '&:hover': active
                              ? { bgcolor: '#0891b2' }
                              : { bgcolor: '#f1f5f9', borderColor: '#cbd5e1' },
                          }}
                        />
                      );
                    })}
                  </div>
                  <div className="panel-body">
                    {filteredNotifications.length === 0 ? (
                      <div style={{ padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                        {notifications.length === 0
                          ? t('noNotifications')
                          : t('noNotificationsForFilter', { defaultValue: 'No matching notifications.' })}
                      </div>
                    ) : (
                      filteredNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`notification-item${notification.is_today ? ' notification-item--today' : ''}`}
style={{
                opacity: !notification.is_today && notification.is_read ? 0.6 : 1,
                cursor: notification.action_url ? 'pointer' : undefined,
              }}
              onClick={() => {
                if (!notification.is_read) {
                  axiosInstance.post(`${API_ENDPOINTS.notifications}${notification.id}/read/`).catch(() => {});
                }
                setShowNotifications(false);
                if (notification.action_url) {
                  navigate(notification.action_url);
                }
              }}
                        >
                          <div className="notification-icon-wrapper">
                            <Bell size={16} />
                          </div>
                          <div className="notification-content">
                            <p className="notification-title">{notification.title}</p>
                            <p className={`notification-message${notification.item_type === 'announcements' || notification.item_type === 'hr_announcement' ? ' notification-message--announcement' : ''}`}>
                              {notification.message}
                            </p>
                            <div className="notification-meta">
                              {notification.is_today && (
                                <span className="notification-today-pill">Today</span>
                              )}
                              <span className="notification-date">
                                {notification.is_today
                                  ? notification.time || notification.created_at
                                  : notification.day && notification.date
                                    ? `${notification.day}, ${notification.date}${notification.time ? ` • ${notification.time}` : ''}`
                                    : notification.created_at}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="panel-footer">
                    <Link
                      to="/notifications"
                      className="panel-footer-link"
                      onClick={() => setShowNotifications(false)}
                    >
                      {t('viewAllNotifications')}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Profile */}
            <div className="profile-section">
              <button
                className="profile-trigger"
                onClick={(e) => {
                  e.stopPropagation();
                  if (mobileMenuOpen) setMobileMenuOpen(false);
                  setShowNotifications(false);
                  setShowLangMenu(false);
                  setShowProfileMenu(!showProfileMenu);
                }}
              >
                <div className="avatar-circle">
                  {avatarContent}
                </div>
                {activeProfile && (
                  <span className="header-profile-name">
                    {activeProfile.type === 'self' ? getUserDisplayName() : activeProfile.name.split(' ')[0]}
                  </span>
                )}
                <ChevronDown size={14} className={`chevron ${showProfileMenu ? 'open' : ''}`} />
              </button>
              {showProfileMenu && (
                <div className="dropdown-panel profile-panel" onClick={(e) => e.stopPropagation()}>
                  <div className="profile-panel-header">
                    <div className="profile-avatar-large">
                      {avatarContent}
                    </div>
                    <div className="profile-info">
                      <h6>{t('welcome', { name: getUserDisplayName() })}</h6>
                      <span className="profile-email">
                        {user?.email && !user.email.endsWith('.internal')
                          ? user.email
                          : user?.username || 'User'}
                      </span>
                      {user?.member_id && (
                        <span className="profile-member-id">{user.member_id}</span>
                      )}
                    </div>
                  </div>
                  <div className="profile-links">
                    {profileMenuItems.map((item, idx) => {
                      const Icon = item.icon;
                      if ('action' in item && item.action) {
                        return (
                          <button
                            key={idx}
                            className="profile-link-item"
                            onClick={item.action}
                          >
                            <span className="profile-link-icon"><Icon size={18} strokeWidth={1.8} /></span>
                            <span>{item.name}</span>
                          </button>
                        );
                      }
                      return (
                        <Link
                          key={idx}
                          to={item.link}
                          className="profile-link-item"
                          onClick={() => setShowProfileMenu(false)}
                        >
                          <span className="profile-link-icon"><Icon size={18} strokeWidth={1.8} /></span>
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                    <div className="profile-divider" />
                    <button
                      className="profile-link-item logout-item"
                      onClick={handleLogout}
                      disabled={isLoading}
                    >
                      <span className="profile-link-icon"><LogOut size={18} strokeWidth={1.8} /></span>
                      <span>{isLoading ? t('profileMenu.loggingOut') : t('profileMenu.logout')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            {false && (
              <button
                className="mobile-toggle"
                onClick={() => {
                  setShowNotifications(false);
                  setShowProfileMenu(false);
                  setShowLangMenu(false);
                  setMobileMenuOpen(!mobileMenuOpen);
                }}
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <>
            <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
            <div className="mobile-drawer">
              <div className="mobile-drawer-header">
                <img src="/images/logo.svg" alt="Infinicare" className="mobile-logo" />
                <button className="mobile-close" onClick={() => setMobileMenuOpen(false)}>
                  <X size={20} />
                </button>
              </div>
                  <div className="mobile-drawer-body">
                <div className="mobile-profile-summary">
                  <div className="avatar-circle large">
                    {avatarContent}
                  </div>
                  <div className="mobile-profile-summary-info">
                    <div className="mobile-profile-name">{getUserDisplayName()}</div>
                    <div className="mobile-profile-email">
                      {(user?.email && !user?.email?.endsWith('.internal')) ? user.email : (user?.username || '')}
                    </div>
                    {user?.member_id && <div className="mobile-profile-badge">{t('profileMenu.memberId')} {user.member_id}</div>}
                  </div>
                </div>
                <div className="mobile-divider" />

                <div className="mobile-section-title">{t('home') || 'Navigation'}</div>
                <Link
                  to="/"
                  className="mobile-nav-item"
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowNotifications(false);
                    setShowLangMenu(false);
                    setMobileMenuOpen(false);
                  }}
                >
                  <Home size={18} />
                  <span>{t('home')}</span>
                </Link>

                <div className="mobile-divider" />
                <div className="mobile-section-title">{t('profileMenu.account') || 'Account'}</div>
                {profileMenuItems.map((item, idx) => {
                  const Icon = item.icon;
                  if ('action' in item && item.action) {
                    return (
                      <button
                        key={idx}
                        className="mobile-nav-item secondary"
                        onClick={() => {
                          setShowProfileMenu(false);
                          setShowNotifications(false);
                          setShowLangMenu(false);
                          setMobileMenuOpen(false);
                          item.action!();
                        }}
                      >
                        <Icon size={16} />
                        <span>{item.name}</span>
                      </button>
                    );
                  }
                  return (
                    <Link
                      key={idx}
                      to={item.link}
                      className="mobile-nav-item secondary"
                      onClick={() => {
                        setShowProfileMenu(false);
                        setShowNotifications(false);
                        setShowLangMenu(false);
                        setMobileMenuOpen(false);
                      }}
                    >
                      <Icon size={16} />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}

                <div className="mobile-divider" />
                <div className="mobile-section-title">{t('common:language.switchLanguage') || 'Preferences'}</div>
                {/* Mobile Language Switcher */}
                <div className="mobile-lang-switcher">
                  <Globe size={16} />
                  <span>{t('common:language.switchLanguage')}</span>
                  <div className="mobile-lang-options">
                    {Object.entries(supportedLanguages).map(([code, lang]) => (
                      <button
                        key={code}
                        className={`mobile-lang-btn${currentLang === code ? ' active' : ''}`}
                        onClick={() => handleChangeLanguage(code)}
                      >
                        {lang.nativeLabel}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mobile-divider" />
                <button
                  className="mobile-nav-item logout"
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowNotifications(false);
                    setShowLangMenu(false);
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                >
                  <LogOut size={18} />
                  <span>{t('profileMenu.logout')}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </nav>
    </header>
  );
};

export default Header;
